// ============================================================================
// transcriptChapters — «главы» расшифровки речи (§2.4): фразовые сегменты
// STT (текст + начало/конец), по которым строится список глав над словами.
// Провайдер сегментов не отдал — список ПУСТ, и панель глав НЕ показывает
// (фейковые метки не создаются).
// Форма сегмента в ответе сервера — одна сущность контракта в разных
// сериализациях: proto-имена (startMs/endMs), Go-JSON (start_ms/end_ms) и
// краткие start/end; разбор принимает их, мусор отбрасывается.
// Чистые функции без React — панель расшифровки использует их напрямую.
// ============================================================================

import { findActiveIntervalIndex } from '@/lib/transcriptWords';

/** Глава расшифровки в секундах (внутренний формат панели). */
export interface TranscriptChapterData {
  text: string;
  /** начало, секунды */
  start: number;
  /** конец, секунды */
  end: number;
}

/** Структурный вид сегмента ответа STT: все поля необязательны — форма
 *  проверяется при разборе, невалидные записи пропускаются. */
export interface RawTranscriptSegment {
  text?: string;
  startMs?: bigint | number;
  endMs?: bigint | number;
  start_ms?: bigint | number;
  end_ms?: bigint | number;
  start?: bigint | number;
  end?: bigint | number;
}

/** Источник сегментов — ответ TranscribeAudio: обязательный текст + поле
 *  segments, когда сервер его отдаёт (описано структурно — SDK может быть
 *  старее сервера и типа поля ещё не знать). */
export interface TranscriptSegmentsSource {
  text: string;
  segments?: readonly RawTranscriptSegment[];
}

/** Метка времени сегмента (мс) → секунды; первое ЗАДАННОЕ имя поля из
 *  сериализаций контракта; нет ни одного — метка не выдумывается (NaN). */
function segmentSeconds(...values: (bigint | number | undefined)[]): number {
  for (const v of values) {
    if (v === undefined) continue;
    const n = Number(v) / 1000;
    if (Number.isFinite(n)) return n;
  }
  return NaN;
}

/** Сегменты ответа → главы (секунды, хронологический порядок). */
export function parseTranscriptChapters(source: TranscriptSegmentsSource): TranscriptChapterData[] {
  const out: TranscriptChapterData[] = [];
  for (const s of source.segments || []) {
    const text = String(s.text || '').trim();
    const start = segmentSeconds(s.startMs, s.start_ms, s.start);
    const end = segmentSeconds(s.endMs, s.end_ms, s.end);
    if (!text || !Number.isFinite(start) || start < 0) continue;
    if (!Number.isFinite(end) || end < start) continue;
    out.push({ text, start, end });
  }
  out.sort((a, b) => a.start - b.start);
  return out;
}

/** Индекс активной главы по текущему времени плеера (-1 — активной нет). */
export function findActiveChapterIndex(chapters: readonly TranscriptChapterData[], currentSec: number): number {
  return findActiveIntervalIndex(chapters, currentSec);
}
